/**
 * The admin Links table's lifecycle status for a coupon.
 *
 * `is_active` alone cannot answer "is this code still usable": it is a MANUAL
 * admin switch that only ever moves when someone clicks Deactivate/Activate.
 * Nothing flips it when a code expires or is fully redeemed, so every code read
 * "Active" forever. Real usability spans four independent axes — enabled? in its
 * time window? redemptions left? a redemption in flight? — and a code can fail
 * several at once, so this collapses them through an explicit precedence ladder
 * rather than a set of independent booleans.
 */
import * as z from 'zod'

import { type AffiliateAdminCouponSchema, couponExpiresAt } from '@/domain/coupons'

export const COUPON_STATUSES = ['INACTIVE', 'EXPIRED', 'USED_UP', 'PENDING', 'ACTIVE'] as const

export type CouponStatus = (typeof COUPON_STATUSES)[number]

/**
 * The list endpoint's per-status distribution, which drives the status pills.
 *
 * It spans every active filter EXCEPT `status`, so the pills keep their counts
 * while the admin clicks between them — which also means `all` equals the
 * response's `total` only while no status filter is set.
 */
export type CouponStatusCounts = { all: number } & Record<CouponStatus, number>

// Annotated rather than inferred so a status added to COUPON_STATUSES fails the
// type check here instead of parsing into a counts object missing a rung.
export const couponStatusCountsSchema: z.ZodType<CouponStatusCounts> = z.object({
  all: z.number(),
  INACTIVE: z.number(),
  EXPIRED: z.number(),
  USED_UP: z.number(),
  PENDING: z.number(),
  ACTIVE: z.number(),
})

/** Stand-in while the first page is still loading, so the pills render nothing
 *  rather than a stale or invented number. Frozen: every consumer shares it. */
export const ZERO_COUPON_STATUS_COUNTS: CouponStatusCounts = Object.freeze({
  all: 0,
  INACTIVE: 0,
  EXPIRED: 0,
  USED_UP: 0,
  PENDING: 0,
  ACTIVE: 0,
})

/** Only the fields the ladder reads — structural, so tests build small fixtures
 *  instead of a full generated coupon. */
export type StatusDerivableCoupon = Pick<
  AffiliateAdminCouponSchema,
  'is_active' | 'created_at' | 'days_valid' | 'max_uses' | 'reservations'
>

/**
 * Redemptions that permanently consumed a slot — reservations a payment
 * actually completed.
 *
 * This is deliberately NOT the backend's cap check
 * (`payments/services/discounts.py`), which counts `converted + in_flight`.
 * That check answers a different question — "may a new user redeem this right
 * now?" — for which a live hold does occupy a slot. The badge answers "what
 * state is this code in?", and those diverge on the one case that matters: a
 * one-use code with a hold in flight has `converted + in_flight >= max_uses`,
 * so the cap reading would call it Used up while nobody has paid a cent and
 * the hold may yet expire and hand the code back.
 *
 * Also NOT `attempted`, which additionally includes `abandoned` — holds already
 * released back to the pool.
 */
const paidRedemptions = (coupon: StatusDerivableCoupon): number => coupon.reservations.converted

/**
 * First match wins. The order is the design decision, not an implementation
 * detail — a code can be disabled AND expired AND capped simultaneously, and
 * these answer different questions:
 *
 * 1. Inactive — an admin kill switch outranks every derived state.
 * 2. Expired  — outside its window; the cap no longer matters.
 * 3. Used up  — PAID redemptions reached the cap. Terminal: money changed
 *               hands and the slot never comes back.
 * 4. Pending  — a redemption is mid-checkout. Transient: the hold may convert
 *               (-> Used up) or expire (-> back to Active), so it must be
 *               distinguishable from a code that genuinely sold out.
 * 5. Active   — enabled, in window, redemptions left, nothing in flight.
 *
 * Used up is tested before Pending because it is the terminal state: an
 * already-exhausted code with a stray hold is finished, not mid-checkout.
 *
 * `now` is injected so one pass over a list evaluates every row against a single
 * instant (and so tests are deterministic).
 */
export const deriveCouponStatus = (
  coupon: StatusDerivableCoupon,
  now: Date = new Date(),
): CouponStatus => {
  if (!coupon.is_active) {
    return 'INACTIVE'
  }

  // `days_valid` has no hard eligibility gate on the backend — it feeds the
  // reservation's expiry (creation + days_valid + a 1h hold grace). Past the
  // window a reservation is still created but is born already-expired, so the
  // code is unusable. The grace is a hold detail, not part of the redemption
  // window, so the badge deliberately does not replicate it.
  const expiresAt = couponExpiresAt(coupon)

  if (expiresAt !== null && expiresAt.getTime() <= now.getTime()) {
    return 'EXPIRED'
  }

  if (coupon.max_uses !== null && paidRedemptions(coupon) >= coupon.max_uses) {
    return 'USED_UP'
  }

  if (coupon.reservations.in_flight > 0) {
    return 'PENDING'
  }

  return 'ACTIVE'
}
